/**
 * Pure helpers for the inline `<cite id="…"/>` citation directive.
 *
 * History (PLT-4590): the chat used to ship `[[cite:<id>]]` markers.
 * They were already backend-emitted so drift was not a real risk
 * (the LLM never produced them), but the chat surface decided to
 * migrate every inline directive to a single HTML-like family for
 * consistency with `<badge>`. The id alphabet, the URL scheme
 * (`cite:<id>`) and the React rendering path are unchanged — only the
 * input grammar changed.
 *
 * Marker grammar (canonical):
 *
 *   `<cite id="abc"/>`     — self-closing
 *   `<cite id="abc"></cite>` — paired form (empty body)
 *
 * Lax forms (forgiveness for hand-typed examples in test fixtures /
 * model emissions if we ever expand the channel):
 *
 *   `<CITE ID="abc"/>`              — case-insensitive
 *   `<cite id='abc'/>`              — single quotes
 *   `<cite id=abc/>`                — unquoted
 *   `<cite  id =  "abc"  />`        — whitespace-tolerant
 *
 * Mirrors the citation registry → render contract: the chat backend
 * (`stream-events.ts`) splices `<cite id="<id>"/>` markers into the
 * streamed text immediately after each `citations_delta` arrives,
 * paired with a `citation-meta` SSE event the client uses to populate
 * its module-level registry. The remark plugin in this folder turns
 * each marker into a `link` mdast node (URL = `cite:<id>`); the
 * `components.a` override looks up the id against the registry and
 * renders the `CitationPill` component.
 *
 * These live in their own module — separate from `remark-citation-pills.ts` —
 * so they can be unit-tested under mocha+ts-node without pulling in
 * the ESM-only `unist-util-visit` (or `react-markdown`) modules that
 * the production plugin and url-transform consumer depend on.
 */
import { findTagsInText, getAttr, parseTagAttributes, type ParsedTag } from './html-tag-parser';

/** URL scheme used for the link nodes minted by `remarkCitationPills`. */
export const CITATION_HREF_PREFIX = 'cite:';

/** Allowed id charset, matching the salt+seq shape minted by `stream-events.ts` (`<hex-salt>-<seq>`). */
const VALID_ID_REGEX = /^[A-Za-z0-9_-]+$/;

/** Mdast `text` node shape (subset). */
export interface MarkerTextNode {
  type: 'text';
  value: string;
}

/** Mdast `link` node minted to replace each marker (subset). */
export interface MarkerLinkNode {
  type: 'link';
  url: string;
  title: null;
  children: MarkerTextNode[];
}

export type MarkerSplitChild = MarkerTextNode | MarkerLinkNode;

/**
 * Split a text string on `<cite>` directives and return the resulting
 * mdast children — text nodes for the spans between directives, link
 * nodes (URL = `cite:<id>`) for each well-formed directive.
 *
 * Returns `null` when the string contains no `<cite>` directives at
 * all, so callers (the remark plugin) can short-circuit without
 * splicing the parent's children.
 *
 * Malformed `<cite>` tags (PLT-4806) — missing `id` attribute, empty
 * id, or id containing characters outside the salt+seq charset — are
 * STRIPPED rather than left as raw text. For the paired form
 * (`<cite index="1">claim</cite>`) the inner text is preserved so the
 * prose still reads naturally; for the self-closing form
 * (`<cite index="1"/>`) the tag drops to nothing. The leak detector
 * (`syntax-leak-detector.ts`) flags the malformed shape independently
 * via the `model_emitted_cite_tag` rule, so visibility for telemetry
 * is no longer load-bearing on this code path.
 *
 * PLT-4945: the model's *native* citation form is the paired shape
 * `<cite index="…">claim</cite>` (Anthropic's `index=` attribute, not
 * the backend's canonical `id=`). When this passes through
 * `remarkParse`/micromark the paired tag is tokenised into THREE
 * separate nodes — an `html` node for the opening `<cite index="…">`,
 * a `text` node for the prose, and an `html` node for the closing
 * `</cite>`. Each of the html fragments reaches this splitter on its
 * own, and a lone open / lone close fragment matches neither branch of
 * `findTagsInText`'s complete-tag grammar (paired or self-closing) — so
 * before this change both fragments fell through and rendered raw. We
 * now additionally detect and strip lone `<cite …>` open fragments and
 * lone `</cite>` close fragments. A lone open carrying a valid `id`
 * still mints a pill (symmetry with the empty-paired `<cite id="x">`
 * form); the inner prose of a *split* paired tag lives in a sibling
 * `text` node we never touch, so it survives untouched.
 *
 * Pure function — no AST mutation, no dependency on `unist-util-visit` —
 * so unit tests can drive it directly.
 */
export function splitTextOnCitationMarkers(text: string): MarkerSplitChild[] | null {
  const spans = collectCiteSpans(text);
  if (spans.length === 0) return null;

  const newChildren: MarkerSplitChild[] = [];
  let lastEnd = 0;
  for (const span of spans) {
    // Both the complete and lone-open forms can carry an `id`. Keep `id`
    // a `const` so TS's aliased-condition narrowing flows the `idIsValid`
    // guard down to the `value: id` use below. (Inner text / self-closing
    // are read inline from the complete span where needed.)
    const id =
      span.kind === 'complete'
        ? getAttr(span.tag.attrs, 'id')
        : span.kind === 'loneOpen'
          ? getAttr(parseTagAttributes(span.attrSource), 'id')
          : undefined;
    const idIsValid = id !== undefined && id.length > 0 && VALID_ID_REGEX.test(id);
    // Emit any preceding prose segment, regardless of whether the span's id
    // is valid — every branch consumes the span, so the surrounding prose
    // still needs to be split out.
    if (span.start > lastEnd) {
      let segment = text.slice(lastEnd, span.start);
      // Strip a single trailing space character before the marker so
      // the inline citation mark hugs the preceding word ("claim¹"
      // rather than "claim ¹"). The visual gap on the *right* of the
      // mark is provided by CSS `margin-right`, which gives a more
      // deliberate, consistent breathing room than the natural prose
      // space character. See the `.chat-citation-mark` rule in
      // `upright-chat-theme.css`. Only strip the space when the tag
      // will actually render — keep the natural spacing intact when
      // we're stripping a malformed tag, so the surrounding prose
      // reads normally.
      if (idIsValid && segment.endsWith(' ')) segment = segment.slice(0, -1);
      // Skip empty segments — happens when two markers are
      // back-to-back with only a space between them, or when the
      // marker is preceded by a single space at the start of the
      // text.
      if (segment.length > 0) {
        newChildren.push({ type: 'text', value: segment });
      }
    }
    if (idIsValid) {
      // The `link` text content is unused by the chat UI's `a`
      // override — it's set to the id solely so the link node is
      // well-formed for any other consumer that might walk the AST.
      newChildren.push({
        type: 'link',
        url: `${CITATION_HREF_PREFIX}${id}`,
        title: null,
        children: [{ type: 'text', value: id }],
      });
    } else if (span.kind === 'complete' && !span.tag.selfClosing && span.tag.innerText.length > 0) {
      // PLT-4806: a `<cite>` tag with a missing / malformed `id` cannot
      // resolve to a known citation, so we strip the tag from the
      // output rather than leaving its source text exposed to the
      // user. For a *complete* paired form (`<cite index="1">claim</cite>`
      // contained in one node) we preserve the inner text so the prose
      // still reads naturally. For self-closing forms there is no inner
      // text, and for split paired forms (PLT-4945) the inner text lives
      // in a sibling `text` node — both drop to nothing here.
      newChildren.push({ type: 'text', value: span.tag.innerText });
    }
    // Everything else — malformed self-closing, malformed lone open, and
    // every lone close — drops to nothing.
    lastEnd = span.end;
  }
  if (lastEnd < text.length) {
    newChildren.push({ type: 'text', value: text.slice(lastEnd) });
  }
  // PLT-4806: always return the split (possibly empty) when at least one
  // `<cite>` span matched in the input — even when every span was malformed
  // and stripped, the original node still has to be replaced with the
  // sanitized result. Returning `null` here would leave the raw `<cite …>`
  // source in the rendered DOM. The no-spans short-circuit above is the
  // only path that legitimately returns `null`.
  return newChildren;
}

/** A `<cite>` span to process in source order — see `collectCiteSpans`. */
type CiteSpan =
  | { kind: 'complete'; start: number; end: number; tag: ParsedTag }
  | { kind: 'loneOpen'; start: number; end: number; attrSource: string }
  | { kind: 'loneClose'; start: number; end: number };

/**
 * Collect every `<cite>` span in `text`, in source order: complete tags
 * (paired or self-closing, matched by `findTagsInText`) plus the lone
 * opening / closing fragments micromark produces when it splits a paired
 * tag across nodes (PLT-4945). Lone fragments that overlap a complete tag
 * (e.g. the opener of a paired tag matched in the same string) are
 * dropped so each source region is represented exactly once.
 */
function collectCiteSpans(text: string): CiteSpan[] {
  const completeTags = findTagsInText(text, 'cite');
  const spans: CiteSpan[] = completeTags.map(tag => ({
    kind: 'complete' as const,
    start: tag.start,
    end: tag.end,
    tag,
  }));

  const overlapsComplete = (start: number, end: number): boolean =>
    completeTags.some(t => start < t.end && end > t.start);

  // Lone opening `<cite …>` fragments. Fresh regex per call so global
  // `lastIndex` state never leaks across invocations (same idiom as the
  // other marker helpers). A self-closing `<cite …/>` is a *complete*
  // tag handled above, so skip any match whose attribute source ends in
  // `/`.
  const openRe = /<cite\b([^>]*)>/gi;
  for (let m = openRe.exec(text); m !== null; m = openRe.exec(text)) {
    const start = m.index;
    const end = start + m[0].length;
    if (m[1].endsWith('/')) continue;
    if (overlapsComplete(start, end)) continue;
    spans.push({ kind: 'loneOpen', start, end, attrSource: m[1].trim() });
  }

  // Lone closing `</cite>` fragments. These never reach `findTagsInText`
  // at all — its cheap `<cite` pre-check fails on `</cite>` — so without
  // this pass a split closing tag leaks raw.
  const closeRe = /<\/cite\s*>/gi;
  for (let m = closeRe.exec(text); m !== null; m = closeRe.exec(text)) {
    const start = m.index;
    const end = start + m[0].length;
    if (overlapsComplete(start, end)) continue;
    spans.push({ kind: 'loneClose', start, end });
  }

  spans.sort((a, b) => a.start - b.start);
  return spans;
}

/**
 * If `href` is a citation marker URL minted by `remarkCitationPills`,
 * return the citation id. Otherwise return null. The chat UI's
 * `components.a` override calls this on every link to decide whether
 * to render a pill or a regular anchor.
 */
export function getCitationIdFromHref(href: string | undefined | null): string | null {
  if (typeof href !== 'string') return null;
  if (!href.startsWith(CITATION_HREF_PREFIX)) return null;
  const id = href.slice(CITATION_HREF_PREFIX.length);
  return id.length > 0 ? id : null;
}

/**
 * Build a `react-markdown` `urlTransform` that lets `cite:<id>` URLs
 * through unchanged and delegates everything else to `defaultTransform`
 * (typically `defaultUrlTransform` from `react-markdown`).
 *
 * Higher-order to keep `react-markdown` (ESM-only) out of this
 * module's import graph — callers in the production code wire in
 * `defaultUrlTransform` themselves; the unit-test wires in a stub.
 *
 * Regression-test target: react-markdown's `defaultUrlTransform`
 * strips any URL whose scheme isn't on its safe-list (replacing it
 * with `""`), so without the `cite:` short-circuit the `a`-component
 * override saw an empty `href` and rendered every pill as plain
 * text. See the bug fixed in PR #7031, commit `2df2a574b8` /
 * `fa792214f0`.
 */
export function makeCitationAwareUrlTransform(defaultTransform: (url: string) => string): (url: string) => string {
  return url => {
    if (url.startsWith(CITATION_HREF_PREFIX)) return url;
    return defaultTransform(url);
  };
}
